<template>
    <div class="h-full flex flex-col space-y-4">
        <div class="flex items-center justify-between space-x-2">
            <div class="flex items-center space-x-3">
                <UButton to="/broadcasts" color="white" icon="i-ph-arrow-left" size="sm" />
                <h1 class="text-2xl font-semibold text-gray-700">Segments</h1>
            </div>
            <div class="flex items-center space-x-2">
                <UButton @click="openCreate()" color="black" icon="i-ph-plus-bold">
                    New Segment
                </UButton>
            </div>
        </div>

        <div class="flex-1 bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
            <UTable
                :rows="segments"
                :columns="columns"
                :loading="loading"
                :loading-state="{ icon: 'i-ph-circle-notch-bold', label: '' }"
                :empty-state="{ icon: 'i-ph-users-three-duotone', label: 'No segments.' }"
                :ui="{ thead: 'sticky top-0 bg-white z-10 shadow-sm', td: { padding: 'py-3 whitespace-pre-wrap' } }"
                class="h-full"
            >
                <template #name-data="{ row }">
                    <div class="w-48">
                        <p class="font-medium">{{ row.name }}</p>
                        <p v-if="row.description" class="text-xs text-gray-500 line-clamp-2">{{ row.description }}</p>
                    </div>
                </template>
                <template #filters-data="{ row }">
                    <div class="flex flex-wrap gap-1.5 whitespace-nowrap max-w-lg">
                        <UBadge v-if="isEmptyFilter(row.filters)" label="All Customers" color="emerald" size="sm" variant="subtle" />
                        <template v-else>
                            <UBadge v-for="tag in row.filters.tags" :key="tag" color="purple" size="sm" variant="subtle">
                                {{ tag }}
                            </UBadge>
                            <UBadge v-if="row.filters.partners?.length" color="blue" size="sm" variant="subtle">
                                {{ row.filters.partners.length }} partner{{ row.filters.partners.length > 1 ? 's' : '' }}
                            </UBadge>
                            <UBadge v-if="row.filters.policy_holders?.length" color="cyan" size="sm" variant="subtle">
                                {{ row.filters.policy_holders.length }} policy holder{{ row.filters.policy_holders.length > 1 ? 's' : '' }}
                            </UBadge>
                            <UBadge v-if="row.filters.country?.values?.length" :color="row.filters.country.exclude ? 'red' : 'indigo'" size="sm" variant="subtle">
                                {{ row.filters.country.exclude ? 'Not in: ' : '' }}{{ formatCountries(row.filters.country.values) }}
                            </UBadge>
                            <UBadge v-if="row.filters.age?.min != null || row.filters.age?.max != null" color="amber" size="sm" variant="subtle">
                                Age {{ row.filters.age?.min ?? '?' }}–{{ row.filters.age?.max ?? '?' }}
                            </UBadge>
                            <UBadge v-if="row.filters.gender?.length" color="pink" size="sm" variant="subtle">
                                {{ formatGender(row.filters.gender) }}
                            </UBadge>
                            <UBadge v-if="formatStatus(row.filters.subscription_status)" color="orange" size="sm" variant="subtle">
                                {{ formatStatus(row.filters.subscription_status) }}
                            </UBadge>
                        </template>
                    </div>
                </template>
                <template #updated_at-data="{ row }">
                    <span class="text-gray-500 text-sm">{{ formatDate(row.updated_at) }}</span>
                </template>
                <template #actions-data="{ row }">
                    <UDropdown :items="actions(row)">
                        <UButton color="gray" variant="ghost" icon="i-heroicons-ellipsis-horizontal-20-solid" />
                    </UDropdown>
                </template>
            </UTable>
        </div>

        <TablePagination :pagination="pagination" class="shrink-0" />

        <OverlaySegmentCreateSegment v-model:open="overlays.create" :seed="seed ?? undefined" @reload="fetchData" />
        <OverlaySegmentEditSegment v-model:open="overlays.edit" @reload="fetchData" :segment="selectedSegment" />
        <OverlaySegmentRemoveSegment v-model:open="overlays.remove" @reload="fetchData" :segment="selectedSegment" />
    </div>
</template>

<script setup lang="ts">
import { UserRole } from '~/types/user'
import type { BroadcastSegment } from '~/types/segment'
import type { CustomerFilter } from '~/types/customer-filter'
import type { SavedView } from '~/types/saved-view'
import { isEmptyFilter } from '~/utils/customer-filter'
import { getCountryName } from '~/utils/countries'

definePageMeta({ layout: 'default', middleware: 'auth', roles: [UserRole.admin] })
useHead({ title: 'Segments' })

const client = useSupabaseClient()
const auth = useAuthStore()
const pagination = usePagination()
const route = useRoute()

const loading = ref(false)
const segments = ref<BroadcastSegment[]>([])
const selectedSegment = ref<BroadcastSegment>()
const savedViews = ref<SavedView[]>([])
const seed = ref<{ filters: CustomerFilter; source_view_id?: string } | null>(null)

const overlays = reactive({
    create: false,
    edit: false,
    remove: false,
})

const columns = [
    { key: 'name', label: 'Name' },
    { key: 'filters', label: 'Filters', class: 'w-full' },
    { key: 'updated_at', label: 'Updated' },
    { key: 'actions', class: 'w-px' },
]

const actions = (segment: BroadcastSegment) => [
    [
        {
            label: 'Edit',
            icon: 'i-ph-pencil-fill',
            click: () => {
                selectedSegment.value = segment
                overlays.edit = true
            },
        },
        {
            label: 'Delete',
            icon: 'i-ph-trash-fill',
            iconClass: 'text-red-400',
            click: () => {
                selectedSegment.value = segment
                overlays.remove = true
            },
        },
    ],
]

onMounted(async () => {
    fetchData()
    if (auth.hasRole(UserRole.admin)) {
        await loadSavedViews()
        const fromView = route.query.fromView as string | undefined
        if (fromView) {
            const view = savedViews.value.find(v => v.id === fromView)
            if (view) seedFromView(view)
        }
    }
})
watch(() => pagination.from, () => fetchData())

async function fetchData() {
    loading.value = true
    segments.value = []
    pagination.setTotal(undefined)

    const { count, data } = await client.from('broadcast_segments')
        .select('*', { count: 'exact' })
        .order('updated_at', { ascending: false })
        .range(pagination.from, pagination.to)
        .returns<BroadcastSegment[]>()

    if (count !== null && data) {
        segments.value = data
        pagination.setTotal(count)
    }

    loading.value = false
}

async function loadSavedViews() {
    try {
        savedViews.value = await useSavedViews().list('customers')
    } catch {
        savedViews.value = []
    }
}

function openCreate() {
    seed.value = null
    overlays.create = true
}

function seedFromView(view: SavedView) {
    seed.value = { filters: view.filters as CustomerFilter, source_view_id: view.id }
    overlays.create = true
}

function formatCountries(codes: string[]): string {
    if (codes.length <= 3) {
        return codes.map(c => getCountryName(c)).join(', ')
    }
    return `${codes.length} countries`
}

function formatGender(genders: ('male' | 'female')[]): string {
    return genders.map(g => g === 'male' ? 'Male' : 'Female').join(', ')
}

function formatStatus(status: unknown): string {
    const list = Array.isArray(status) ? status : status ? [status] : []
    return list.map(s => String(s).charAt(0).toUpperCase() + String(s).slice(1)).join(', ')
}

function formatDate(date: string): string {
    return new Date(date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    })
}
</script>
